import axios from 'axios'
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import 'firebase/compat/firestore'
import 'firebase/compat/installations'
import 'firebase/compat/storage'
import { getMessaging, getToken, onMessage } from 'firebase/messaging'
import { getRemoteConfig, getValue } from 'firebase/remote-config'
import { useEffect } from 'react'

import firebaseWebConfig from '../firebase-web-config.json'
import { getParams } from '../hooks'
import {
  initCloseStripeButton,
  Config,
  generateFirestoreId,
  readableError,
  ResourceTypeConstant,
  showStripe,
  VariableSourceType,
} from '../utils'
import { FF, FirebaseContext, FNP, FP, FPVT, FV } from './index'

export interface FirebaseConfig {
  [key: string]: string
}

interface Props {
  children: React.ReactNode
  config: Config
  webFirebaseConfig?: FirebaseConfig
  firestorePrefix?: string
}

export const FirebaseProvider: React.FC<Props> = ({ children, config, webFirebaseConfig, firestorePrefix = '' }) => {
  const emailKey = 'email'

  const firebaseConfig = webFirebaseConfig || (firebaseWebConfig as FirebaseConfig)

  const app = firebase.initializeApp(firebaseConfig)

  const auth = firebase.auth()

  const firestore = firebase.firestore(app)

  const storage = firebase.storage(app)

  const remoteConfig = getRemoteConfig(app)
  remoteConfig.settings.minimumFetchIntervalMillis = 0

  const messaging = getMessaging(app)

  onMessage(messaging, payload => {
    const { title = '', body, icon } = payload.notification || {}
    new Notification(title, { body, icon })
  })

  const googleProvider = new firebase.auth.GoogleAuthProvider()
  googleProvider.setCustomParameters({ prompt: 'select_account' })

  const signInWithGoogle = async () => {
    await auth.signInWithPopup(googleProvider)
  }

  const createUserWithEmailAndPassword: FP = async (...props) => {
    const { email, password } = await getParams(...props)
    await auth.createUserWithEmailAndPassword(email || '', password || '')
  }

  const signInWithEmailAndPassword: FP = async (...props) => {
    const { email, password } = await getParams(...props)
    await auth.signInWithEmailAndPassword(email || '', password || '')
  }

  const sendPasswordResetEmail: FV = async (getVariableValue, variable) => {
    const email = (await getVariableValue(variable)) || ''
    await auth.sendPasswordResetEmail(email).then(() => alert(`Link was sent to ${email}`))
  }

  const sendSignInLinkToEmail: FV = async (getVariableValue, variable) => {
    const email = (await getVariableValue(variable)) || ''
    await auth
      .sendSignInLinkToEmail(email, { url: window.location.origin, handleCodeInApp: true })
      .then(() => localStorage.setItem(emailKey, email))
  }

  const reSendSignInLinkToEmail: FV = async (...props) => {
    const result = window.confirm('Trouble getting email? Resend?')
    if (result) {
      await sendSignInLinkToEmail(...props)
    }
  }

  const getAssetRecord: FF = (getVariableValue, file) =>
    new Promise(async resolve => {
      const currentUserId = await getVariableValue({
        source: { type: VariableSourceType.globalVariable, variableName: 'currentUserId' },
      })
      const { name: title, type: mimeType } = file
      const [type, format] = mimeType.split('/')
      const storageRef = storage.ref(`uploads/${auth.currentUser?.uid}/${generateFirestoreId()}/${title}`)
      const acceptValues: ResourceTypeConstant[] = [
        ResourceTypeConstant.image,
        ResourceTypeConstant.video,
        ResourceTypeConstant.audio,
        ResourceTypeConstant.file,
      ]
      const resourceType = acceptValues.includes(type) ? type : ResourceTypeConstant.file
      storageRef
        .put(file)
        .then(() =>
          storageRef.getDownloadURL().then(url =>
            resolve({
              bytes: null,
              duration: null,
              error: null,
              folder: '~',
              format,
              height: null,
              originalFileUri: null,
              pageCount: null,
              resourceType,
              signature: null,
              status: null,
              thumbnailUrl: null,
              title,
              url,
              version: null,
              width: null,
              mimeType,
              ownerId: currentUserId,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            })
          )
        )
        .catch(err => alert(readableError(err.message)))
        .finally(() => resolve(null))
    })

  const httpClient = axios.create({ headers: { 'Content-Type': 'application/json' } })

  httpClient.interceptors.request.use(
    async config => {
      const accessToken = await auth.currentUser?.getIdToken()
      if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`
      }
      return config
    },
    err => Promise.reject(err)
  )

  httpClient.interceptors.response.use(
    res => res,
    async err => {
      if (err.response) {
        const originalConfig = err.config
        if (err.response.status === 401 && !originalConfig._retry) {
          originalConfig._retry = true
          const accessToken = await auth.currentUser?.getIdToken(true)
          if (accessToken) {
            httpClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          }
          return httpClient(originalConfig)
        }
      }
      return Promise.reject(err)
    }
  )

  const getApiUrl = () => {
    const errorMessage = 'Backend is not deployed.'
    const key = config.data.remoteConfig?.apiUrl
    if (!key) {
      throw new Error(errorMessage)
    }
    const apiUrl = getRemoteConfigValue(key)
    if (!apiUrl) {
      throw new Error(errorMessage)
    }
    return apiUrl
  }

  const getRemoteConfigValue = (key: string) => getValue(remoteConfig, key).asString()

  const getInstallationId = () => app.installations().getId()

  const getNotificationToken = () => getToken(messaging, { vapidKey: firebaseConfig.vapidKey })

  const setNotificationToken = async () => {
    const apiUrl = getApiUrl()
    const installationId = await getInstallationId()
    const token = await getNotificationToken()
    await httpClient.post(`${apiUrl}/v1/auth/devices`, {
      id: installationId,
      fcmToken: token,
      platform: 'web',
      name: window.navigator.userAgent,
    })
  }

  const deleteNotificationToken = async () => {
    const apiUrl = getApiUrl()
    const installationId = await getInstallationId()
    await httpClient.delete(`${apiUrl}/v1/auth/devices/${installationId}`)
  }

  const checkNotificationsEnabled = async () => {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        await setNotificationToken()
        return true
      } catch {
        return false
      }
    } else {
      return false
    }
  }

  const enableNotifications = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      if (permission === 'granted') {
        await setNotificationToken()
        return
      }
    }
    throw new Error('User Notifications Permission Denied. Enable Notifications in browser settings.')
  }

  const logout = async () => {
    try {
      await deleteNotificationToken()
    } catch {}
    await auth.signOut()
  }

  const runFlow: FNP = async (name, getVariableValue, parameters) => {
    const apiUrl = getApiUrl()
    const data = parameters ? await getParams(getVariableValue, parameters) : undefined
    return httpClient.post(`${apiUrl}/v1/flows/${name}`, data)
  }

  const search = (url: string) => {
    const apiUrl = getApiUrl()
    return httpClient.get(`${apiUrl}/v1/${url}`)
  }

  const oauth: FPVT = (getVariableValue, parameters, valueTarget) =>
    new Promise(async (resolve, reject) => {
      const { provider, scopes } = await getParams(getVariableValue, parameters)
      if (provider === 'google' && Array.isArray(scopes)) {
        const errorMessage = 'No oAuthClientId.'
        const key = config.data.remoteConfig?.oAuthClientId
        if (!key) {
          throw new Error(errorMessage)
        }
        const client_id = getRemoteConfigValue(key)
        if (!client_id) {
          throw new Error(errorMessage)
        }
        const codeClient = google.accounts.oauth2.initCodeClient({
          client_id,
          scope: scopes.join(' '),
          ux_mode: 'popup',
          callback: async ({ code }) => {
            const value = JSON.stringify({ client_id, redirect_uri: 'postmessage', scopes, code })
            await getVariableValue({ source: valueTarget }, undefined, { value })
            resolve()
          },
          error_callback: err => reject(err),
        })
        codeClient.requestCode()
      } else {
        reject(new Error('Something went wrong!'))
      }
    })

  const checkoutSelected = (priceId: string) =>
    new Promise((resolve, reject) => {
      const apiUrl = getApiUrl()
      httpClient
        .post(`${apiUrl}/v1/integrations/stripe/checkout-sessions`, { priceId })
        .then(async res => {
          const { id, clientSecret } = res.data
          try {
            // @ts-ignore
            const stripe = Stripe(getRemoteConfigValue('stripePublishableKey'))
            const checkout = await stripe.initEmbeddedCheckout({
              clientSecret,
              onComplete: () =>
                httpClient
                  .patch(`${apiUrl}/v1/integrations/stripe/checkout-sessions/${id}`)
                  .finally(() => closeStripeButton.click()),
            })
            showStripe()
            checkout.mount('#stripe')
            const closeStripeButton = initCloseStripeButton(() => {
              checkout.destroy()
              resolve(null)
            })
          } catch (err: any) {
            reject(err)
          }
        })
        .catch(err => reject(err))
    })

  useEffect(() => {
    const googleMaps = document.getElementById('googleMaps') as HTMLScriptElement
    googleMaps.setAttribute(
      'src',
      `https://maps.googleapis.com/maps/api/js?key=${firebaseConfig.apiKey}&loading=async&callback=Function.prototype`
    )
  }, [])

  return (
    <FirebaseContext.Provider
      value={{
        f: {
          firestorePrefix,
          auth,
          firestore,
          remoteConfig,
          getRemoteConfigValue,
          signInWithGoogle,
          createUserWithEmailAndPassword,
          signInWithEmailAndPassword,
          sendPasswordResetEmail,
          sendSignInLinkToEmail,
          reSendSignInLinkToEmail,
          getAssetRecord,
          checkNotificationsEnabled,
          enableNotifications,
          logout,
          runFlow,
          search,
          oauth,
          checkoutSelected,
          emailKey,
        },
      }}
    >
      {children}
    </FirebaseContext.Provider>
  )
}
